<template>
  <v-card>
    <v-card-title class="d-flex justify-space-between align-center mx-2">
      {{ $t('project.members.dialogs.GroupImportDialog.title') }}
      <AppButton icon @click="cancel">
        <app-icon icon-name="xmark" icon-weight="fal" size="regular"/>
      </AppButton>
    </v-card-title>
    <v-card-text>
      <p class="mb-4">
        {{ $t('project.members.dialogs.GroupImportDialog.textInfo') }}
      </p>
      <v-row>
        <v-col>
          <AppDownloadFile :link="templateLink"
                           class="w-100 h-100"
          >
            <template #default="{submitForm}">
              <AppCard class="GroupImportDialog-card text-center clickable h-100"
                       hover
                       @click="submitForm">
                <div class="d-inline-flex align-center ml-n3">
                  <div class="mr-3 step-chip">1</div>
                  <h3>{{ $t('project.members.dialogs.GroupImportDialog.downloadModel') }}</h3>
                </div>
                <div class="my-5">
                  <v-img src="@/assets/img/icons/excel_import.svg"
                         class="mx-auto"
                         width="70"
                  />
                </div>
              </AppCard>
            </template>
          </AppDownloadFile>
        </v-col>
        <v-col>
          <AppCard class="GroupImportDialog-card text-center clickable h-100"
                   hover
                   @click="onFileInputClick">
            <div class="d-inline-flex align-center ml-n3">
              <div class="mr-3 step-chip">2</div>
              <h3>{{ $t('project.members.dialogs.GroupImportDialog.uploadModel') }}</h3>
            </div>
            <div class="my-5">
              <v-img src="@/assets/img/icons/excel_closd.svg"
                     class="mx-auto"
                     width="70"
              />
            </div>
            <div v-if="groupImportFormModel.selectedFile">{{ groupImportFormModel.selectedFile.name }}</div>
            <input ref="fileInput" type="file" class="d-none" name="fileInput" @input="selectFile"/>
          </AppCard>
        </v-col>
      </v-row>
      <v-row v-if="errorCode">
        <v-col>
          <div class="text-error text-center">
            {{ $t('project.members.dialogs.GroupImportDialog.error.massImport_' + errorCode) }}
            <template v-if="errorCode === 'ERR_INCORRECT_DATA'">
              <ul style="list-style-position: inside;" class="text-left">
                <li v-for="(error, i) in errorDetails" :key="`errorLine-${i}`" class="py-1">
                  <!--
                    Number is incremented by 2 because the errors from the back-end are indexed from 0 and
                    there's also the header that's ignored by the parser but needs to be included to be accurate
                  -->
                  {{ $t('project.members.dialogs.GroupImportDialog.error.massImport_atLine') }} {{ Number(i) + 2 }}
                  <ul style="list-style-position: inside;" class="ml-5">
                    <li v-for="(errorDetail, j) in error" :key="`error-${j}`">
                      {{ $t("project.members.dialogs.GroupImportDialog.error.massImport_" + errorDetail.errorCode) }}
                    </li>
                  </ul>
                </li>
              </ul>
            </template>
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-checkbox v-model="groupImportFormModel.sendInvitationNow"
                      hide-details
                      density="compact"
                      @update:model-value="onSendInvitationNowChange"
          >
            <template #label>
              <span class="mr-2">{{ $t('project.members.dialogs.GroupImportDialog.sendInvitationNow') }}</span>
              <AppTooltip>
                <template #activator="{ props }">
                  <app-icon icon-name="circle-question"
                            icon-weight="fas"
                            size="small"
                            style="color: #ababab"
                            v-bind="props"
                  />
                </template>
                <span>{{ $t('project.members.dialogs.GroupImportDialog.sendInvitationNowTooltip') }}</span>
              </AppTooltip>
            </template>
          </v-checkbox>

          <v-checkbox v-if="!isDataRoom"
                      v-model="groupImportFormModel.idVerification"
                      density="compact"
                      hide-details
          >
            <template #label>
              <span class="mr-2">{{ $t('project.members.dialogs.GroupImportDialog.idVerification') }}</span>
              <AppTooltip>
                <template #activator="{ props }">
                  <app-icon icon-name="circle-question"
                            icon-weight="fas"
                            size="small"
                            style="color: #ababab"
                            v-bind="props"
                  />
                </template>
                <span>{{ $t('project.members.dialogs.GroupImportDialog.idVerificationTooltip') }}</span>
              </AppTooltip>
            </template>
          </v-checkbox>

          <v-checkbox v-model="groupImportFormModel.hidePm"
                      density="compact"
                      hide-details
          >
            <template #label>
              <span class="mr-2">{{ $t('project.members.dialogs.GroupImportDialog.hidePm') }}</span>
              <AppTooltip>
                <template #activator="{ props }">
                  <app-icon icon-name="circle-question"
                            icon-weight="fas"
                            size="small"
                            style="color: #ababab"
                            v-bind="props"
                  />
                </template>
                <span>{{ $t('project.members.dialogs.GroupImportDialog.hidePmTooltip') }}</span>
              </AppTooltip>
            </template>
          </v-checkbox>

          <v-checkbox v-model="groupImportFormModel.customMessageIsActive"
                      :disabled="!groupImportFormModel.sendInvitationNow"
                      density="compact"
                      hide-details
          >
            <template #label>
              <span>{{ $t('project.members.dialogs.GroupImportDialog.customMessageIsActive') }}</span>
            </template>
          </v-checkbox>
          <template v-if="groupImportFormModel.customMessageIsActive">
            <VisualTextEditor v-model:value="groupImportFormModel.customMessage"
                              counter="footer"
                              class="mt-2"
                              :error-messages="customMessageErrors"
                              :max-length="customMessageMaxLength"
            />
          </template>
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-actions class="pa-6">
      <v-spacer></v-spacer>
      <AppButton @click="cancel">
        {{ $t('common.cancel') }}
      </AppButton>
      <AppButton color="primary"
                 :disabled="okDisabled"
                 :loading="okLoading"
                 @click="validate"
      >
        {{ $t('project.members.dialogs.GroupImportDialog.import') }}
      </AppButton>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex'

import AppCard from '@/common/AppCard.vue'
import AppDownloadFile from '@/common/AppDownloadFile.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import AppButton from '@/common/buttons/AppButton.vue'
import { escapeHtml } from '@/common/utils/strings'
import VisualTextEditor from '@/common/visual-text-editor/VisualTextEditor.vue'

const CUSTOM_MESSAGE_MAX_LENGTH = 400
export default {
  name: 'GroupImportForm',
  components: {
    AppButton,
    AppCard,
    AppDownloadFile,
    AppTooltip,
    VisualTextEditor,
  },
  props: {
    groupImportFormModel: {
      type: Object,
      required: true,
    },
    errorCode: {
      type: String,
      default: '',
    },
    errorDetails: {
      type: Array,
      default: () => ([]),
    },
    okLoading: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      customMessageMaxLength: CUSTOM_MESSAGE_MAX_LENGTH,
    }
  },
  computed: {
    ...mapGetters('room', ['isDataRoom']),

    customMessageLength () {
    return escapeHtml(this.groupImportFormModel.customMessage).length
    },
    customMessageIsTooLong () {
      return this.groupImportFormModel.customMessageIsActive && this.customMessageLength > this.customMessageMaxLength
    },
    customMessageErrors () {
      const errors = []

      if (this.customMessageIsTooLong) {
        errors.push(this.$t('common.validations.textTooLong'))
      }

      return errors
    },
    okDisabled () {
      return !this.groupImportFormModel.selectedFile || this.customMessageIsTooLong
    },
    templateLink () {
      return `${import.meta.env.VUE_APP_API_URL}/resources/${this.isDataRoom ? 'massImportTemplateDataroom' : 'massImportTemplate'}`
    },
  },
  methods: {
    selectFile (e) {
      this.$emit('update:errorCode', null)

      if (e.target.files.length > 0) {
        this.groupImportFormModel.selectedFile = e.target.files[0]
        // Check for file size
        if (this.groupImportFormModel.selectedFile.size > 10 * 1024 * 1024) {
          this.$emit('update:errorCode', 'FILE_TOO_BIG')
          return
        }

        // Check for file type
        const ext = this.groupImportFormModel.selectedFile.name.split('.').pop()
        if (ext !== 'csv' && ext !== 'xls' && ext !== 'xlsx') {
          this.$emit('update:errorCode', 'WRONG_FILE_TYPE')
        }
      }
    },
    onFileInputClick () {
      this.$refs.fileInput.click()
    },
    onSendInvitationNowChange () {
      this.groupImportFormModel.customMessageIsActive = false
      this.groupImportFormModel.customMessage = ''
    },
    // Public use function. Make the file input empty again.
    resetFileInput () {
      this.$refs.fileInput.value = ''
    },
    cancel () {
      this.$emit('onCancel')
    },
    validate () {
      this.$emit('onValidate')
    },
  },
}
</script>

<style scoped lang="scss">
.GroupImportDialog-card {
  border-radius: 7px;
}

.step-chip {
  background: rgb(var(--v-theme-tertiary));
  color: white;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
